import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCreateAdjustment } from '@/hooks/useAdjustments'
import { useStocktakeFilterOptions } from '@/hooks/useStocktakes'

const selectCls =
  'h-8 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-xs text-[var(--text-primary)] disabled:text-[var(--mute)] focus:outline-none focus:border-[var(--brand)]'
const inputCls = 'h-8 text-xs'

// Same default as the stocktake modal — the correction is almost always at the
// store.
const DEFAULT_LOCATION_NAME = 'brewster store'

const schema = z.object({
  location_id: z.string().min(1, 'Pick a location'),
  // Both free-text and both optional: Stocky lets a one-off go out unlabelled,
  // and the record is still identified by its number.
  reason: z.string(),
  notes: z.string(),
})

type NewAdjustmentValues = z.infer<typeof schema>

const EMPTY: NewAdjustmentValues = { location_id: '', reason: '', notes: '' }

export interface NewAdjustmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Opens an EMPTY draft and takes the operator to it — the variants are picked
 *  on the detail screen, one line at a time, because a manual adjustment isn't
 *  scoped to a vendor the way a stocktake is. */
export function NewAdjustmentDialog({ open, onOpenChange }: NewAdjustmentDialogProps) {
  const navigate = useNavigate()
  const create = useCreateAdjustment()
  const busy = create.isPending

  const form = useForm<NewAdjustmentValues>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  })

  // Locations come from the stocktake filter-options endpoint — the same
  // Shopify location mirror both modules write against, so there is no second
  // list to keep in step.
  const { data: options } = useStocktakeFilterOptions()
  const locations = options?.locations ?? []

  React.useEffect(() => {
    if (!open) return
    form.reset(EMPTY)
    create.reset()
    // Reset on open only — the mutation object identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  React.useEffect(() => {
    if (!open || locations.length === 0) return
    if (form.getValues('location_id')) return
    const preferred =
      locations.find((l) => l.name.trim().toLowerCase() === DEFAULT_LOCATION_NAME) ??
      locations[0]
    form.setValue('location_id', String(preferred.location_id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, locations])

  async function onSubmit(values: NewAdjustmentValues) {
    try {
      const detail = await create.mutateAsync({
        location_id: Number(values.location_id),
        reason: values.reason.trim() || undefined,
        notes: values.notes.trim() || undefined,
      })
      onOpenChange(false)
      navigate(`/adjustments/${detail.adjustment_id}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create the adjustment')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent
        className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[520px]"
        data-testid="new-adjustment-dialog"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">Create Adjustment</DialogTitle>
          <DialogDescription className="text-xs text-[var(--mute)]">
            Opens an empty draft. Add the variants you want to correct, set each new
            quantity, then apply the whole draft to Shopify in one push.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            noValidate
            className="flex flex-col gap-3"
          >
            <FormField
              control={form.control}
              name="location_id"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Location</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={busy}
                      className={selectCls}
                      data-testid="adjustment-location"
                    >
                      <option value="">— select —</option>
                      {locations.map((l) => (
                        <option key={l.location_id} value={String(l.location_id)}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Reason (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      disabled={busy}
                      className={inputCls}
                      placeholder="e.g. Damaged in the stockroom"
                      data-testid="adjustment-reason"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem className="gap-1">
                  <FormLabel className="text-xs">Notes (optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={busy}
                      className={inputCls}
                      placeholder="Anything the next person should know"
                      data-testid="adjustment-notes"
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="h-7 text-xs"
                disabled={busy}
                data-testid="adjustment-submit"
              >
                {busy ? 'Creating…' : 'Create Adjustment'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
